import React, { useEffect } from "react";
import { useValidityFilter } from "../../../state/FilterContext";
import { useArrayContext } from "../../../state/ArrayContext";
import { useSiteContext } from "../../../state/SiteContext";
import { SourceItem } from "../../../shared/models/source-item.model";
import { ValidityDefinition } from "../../../shared/models/site.model";

/***
 * Array source that returns the steps of a validity path leading up to the current validity.
 * The validity path is a string of the form "validity:path:value".
 * 
 * For each step in the path, the source returns s SourceItem describing the step,
 * with the following properties:
 * 
 * - value: The validity path to the step, which is the values of the previous steps joined by ":".
 * - position: The position of the item in the array.
 * - props.info.path: The path to the step.
 * - props.info.name: The name of the property that the step affects.
 * - props.info.value: The value of the property that the step affects.
 * 
 * The array includes all the steps in the path, excluding any preview steps, plus the next step
 * in the path if one is defined for the site.
 */
export const ValidityPath = React.memo(function ValidityPath(): React.ReactElement {
    const { setArray } = useArrayContext();
    const { siteState } = useSiteContext();
    const validityFilter = useValidityFilter();

    useEffect(() => {
        const result: Array<SourceItem> = [];
        let definition: ValidityDefinition | null = siteState.site?.validities || null;
        const currentValidity: Array<string> = (validityFilter || "").split(":");
        const path: Array<string> = [];
        if (currentValidity[0]?.startsWith("$")) {
            // Don't include values starting with $ because they are preview validities
            currentValidity.shift();
        }

        while (definition) {
            const pathstring = path.join(":");
            const value = currentValidity.shift() || definition.default || "";
            const item: SourceItem = {
                value: pathstring,
                position: path.length + 1,
                props: {
                    info: {
                        "path": [pathstring],
                        "name": [definition.name],
                        "value": [value],
                    }
                },
            }
            path.push(value);
            result.push(item);
            const pos: number = definition?.values?.findIndex((loopitem) => loopitem.value === value) ?? -1;
            definition = definition.values?.[pos]?.definition || null;
        }
        setArray(result);
    }, [validityFilter, siteState, setArray]);

    return <></>;
});